<script setup lang="ts">
import type { Schemas } from "#shopware";
import { getCategoryUrl, getTranslatedProperty } from "@shopware/helpers";
import { Swiper, SwiperSlide } from "swiper/vue";
import { FreeMode, Mousewheel } from "swiper/modules";
import type { AssortimentStoryblok } from "~/types/storyblok";

const modules = [FreeMode, Mousewheel];
const defaultLimit = 20;
const defaultPage = 1;

const route = useRoute();

const { onViewItemList, onSelectItem } = useGtagEvents();
const { renderMode } = useProductListingView();
const { clickedObjectIDs } = useAlgoliaSearchInsights();
const { reviewIds } = useReviewIds();
const { currentCategory, baseCategories, subCategories } = useShopwareCategories();
const { story, error: storyError } = await useStoryblokRouter<AssortimentStoryblok>(route.path);

if (!currentCategory.value) {
	throw createError({ statusCode: 404, statusMessage: `Category ${route.path} not found` });
}

const title = computed<string>(() => currentCategory.value?.metaTitle ?? currentCategory.value?.name ?? "");
const description = computed<string>(() => currentCategory.value?.metaDescription ?? "");

useCustomSeoMeta({
	title,
	description,
	image: toRef(currentCategory.value?.media?.url ?? `/default-kippie-category-image.jpeg`)
});

useHead({
	title: title.value
});

const limitQuery = route.query.limit ? Number(route.query.limit) : defaultLimit;
const pageQuery = route.query.p ? Number(route.query.p) : defaultPage;
const orderQuery = route.query.order ? (route.query.order as string) : undefined;

const { search, getElements, loading, getTotal, loadMore, loadingMore, resetFilters, getCurrentListing } = useListing({
	listingType: "categoryListing",
	categoryId: currentCategory.value?.id,
	//@ts-expect-error
	defaultSearchCriteria: {
		limit: defaultLimit,
		page: pageQuery,
		order: orderQuery,

		// reduceAggregations: true,
		associations: {
			crossSellings: {
				limit: 1,
				filter: [
					{
						type: "equals",
						field: "active",
						value: true
					}
				]
			}
		},
		includes: {
			product: [
				"id",
				"name",
				"translated",
				"cover",
				"customFields",
				"seoUrls",
				"calculatedPrice",
				"crossSellings"
			],
			product_cross_selling: ["id", "name", "translated.name"]
		}
	}
});

const _loadMore = () => {
	//@ts-expect-error

	loadMore({
		limit: defaultLimit,
		p: getCurrentListing.value?.page + 1,
		order: orderQuery,

		// reduceAggregations: true,
		associations: {
			crossSellings: {
				limit: 1,
				filter: [
					{
						type: "equals",
						field: "active",
						value: true
					}
				]
			}
		},
		includes: {
			product: [
				"id",
				"name",
				"translated",
				"cover",
				"customFields",
				"seoUrls",
				"calculatedPrice",
				"crossSellings"
			],
			product_cross_selling: ["id", "name", "translated.name"]
		}
	});
};

const onProductCardClick = (product: Schemas["Product"]) => {
	if (currentCategory.value && product) {
		try {
			onSelectItem(currentCategory.value, product);
		} catch (e) {
			console.error("Error while sending view_item event", e);
		}
	}
	clickedObjectIDs([product.id]);
};

await search({
	...route.query,
	limit: limitQuery,
	page: pageQuery,
	order: orderQuery
});

if (currentCategory.value && getElements.value) {
	try {
		onViewItemList(currentCategory.value, getElements.value);
	} catch (e) {
		console.error("Error while sending view_item_list event", e);
	}
}

onMounted(() => {
	useReviewParameters();
});
</script>

<template>
	<Container class="py-6 overflow-hidden">
		<client-only>
			<CreateProductsReviewsForm v-if="reviewIds.length" :product-ids="reviewIds" />
		</client-only>
		<div class="flex flex-col gap-4">
			<Breadcrumbs :crumbs="currentCategory?.breadcrumb" />
			<Typography variant="h1" class="leading-0">
				{{ getTranslatedProperty(currentCategory, "name") }}
			</Typography>
			<KippieText
				:clamp="2"
				read-more="Lees meer"
				read-less="Lees minder"
				v-if="currentCategory && currentCategory?.description"
			>
				<span>
					{{ getTranslatedProperty(currentCategory, "description").replace(/<\/?[^>]+>/gi, " ") }}
				</span>
			</KippieText>
		</div>

		<!-- Filters -->
		<div class="py-8 flex lg:gap-10">
			<AssortmentFilterAndSort />

			<CategoriesCarousel @on-navigation="resetFilters()" />
		</div>

		<div class="flex gap-3 pb-6" v-if="subCategories!.length > 0">
			<Swiper
				v-if="!!baseCategories"
				space-between="8px"
				slides-per-view="auto"
				free-mode
				mousewheel
				:modules="modules"
				class="!-mx-6 !px-6"
				wrapper-class="flex"
			>
				<SwiperSlide v-for="category in subCategories" :key="category.id" class="max-w-min mr-2">
					<NuxtLink :to="getCategoryUrl(category as any)" @click="resetFilters()">
						<KippieButton
							:color="route.fullPath.includes(getCategoryUrl(category as any)) ? 'black' : 'white'"
							size="small"
						>
							{{ category.name }}
						</KippieButton>
					</NuxtLink>
				</SwiperSlide>
			</Swiper>
		</div>

		<!-- Product count and render mode -->
		<div class="mb-4 flex justify-between items-center">
			<span v-if="!loading" class="text-gray font-medium text-xs">
				{{ getTotal }} {{ getTotal === 1 ? "product" : "producten" }}
			</span>

			<div class="flex gap-4 visible md:invisible">
				<SvgIcon
					name="single"
					class="cursor-pointer"
					:class="{ 'text-gray': renderMode === 'double' }"
					@click="renderMode = 'single'"
				/>
				<SvgIcon
					name="double"
					class="cursor-pointer"
					:class="{ 'text-gray': renderMode === 'single' }"
					@click="renderMode = 'double'"
				/>
			</div>
		</div>

		<!-- Product listing -->
		<div
			class="rounded-lg flex flex-col items-center justify-center gap-6 bg-white pt-6 px-4 text-sm pb-0 overflow-hidden max-w-lg mx-auto"
			v-if="loading"
		>
			<span class="text-gray text-center">We laden de lekkerste Kippie producten voor jou...</span>
			<KippieLoader class="text-gray -mb-[4px]" />
		</div>
		<div
			class="grid xl:grid-cols-4 lg:grid-cols-3 md:grid-cols-2 gap-x-4 md:gap-x-8 gap-y-6 auto-cols-fr"
			:class="{
				'grid-cols-1': renderMode === 'single',
				'grid-cols-2 -mx-6 md:mx-0': renderMode === 'double'
			}"
			v-else
		>
			<ListingProductCard
				v-for="product in getElements"
				:key="product.id"
				:product="product"
				:render-mode="renderMode"
				@click="onProductCardClick(product)"
			/>
			<template v-if="story?.content?.banners.length && !storyError">
				<ContentBanner
					v-for="(banner, index) in story.content.banners.slice(0, 2)"
					:key="banner.title"
					:blok="banner"
					class="md:col-span-2 md:rounded-3xl overflow-hidden"
					:class="{
						'row-start-5 md:row-start-2': index === 0,
						'row-start-9 md:row-start-4 xl:!col-start-3 lg:!col-start-2': index === 1,
						'col-span-2': renderMode === 'double',
						'-mx-6 md:mx-0': renderMode === 'single',
						hidden: getTotal === 0 || (getTotal < 5 && index === 0) || (getTotal < 11 && index === 1)
					}"
				/>
			</template>
		</div>

		<!-- No results message -->
		<EmptyMessage
			v-if="getTotal === 0 && !loading"
			class="md:w-1/2"
			title="Geen producten gevonden"
			message="Probeer een andere filter of categorie voor meer resultaten"
		/>

		<!-- Load more button -->
		<div
			class="flex justify-center py-8 md:w-1/2 lg:w-1/3 mx-auto"
			v-if="getElements && getElements.length !== getTotal"
		>
			<KippieButton color="black" block @click="_loadMore()" :loading="loadingMore"> Toon meer </KippieButton>
		</div>
	</Container>
</template>

<style scoped>
.swiper {
	overflow-y: visible;
}
</style>
